<template>
  <div class="dict-select-wrap">
    <el-select
      :value="value"
      filterable
      :multiple="multiple"
      :filter-method="onFilter"
      :clearable="clearable"
      :placeholder="placeholder"
      :disabled="disabled"
      popper-class="dict-select-dropdown"
      style="width:100%"
      @visible-change="onVisibleChange"
      @input="onSelectInput"
      @change="onSelectChange"
    >
      <el-option
        v-for="d in filteredOptions"
        :key="d.id"
        :label="d.dictLabel"
        :value="optionValue(d)"
      />
      <el-option
        v-if="!disabled"
        :value="ADD_VALUE"
        :label="addText"
        class="dict-add-option"
      >
        <span class="add-opt-text" @click.stop.prevent="openAdd">+ {{ addText }}</span>
      </el-option>
    </el-select>

    <el-dialog
      title="新增字典项"
      :visible.sync="visible"
      width="480px"
      append-to-body
      @close="resetForm"
    >
      <el-form :model="form" label-width="90px">
        <el-form-item label="字典类型" required>
          <el-input v-model="form.dictType" disabled />
        </el-form-item>
        <el-form-item label="选项名称" required>
          <el-input v-model="form.dictLabel" :placeholder="labelPlaceholder" />
        </el-form-item>
        <el-form-item label="编码">
          <el-input v-model="form.dictValue" placeholder="可选" />
        </el-form-item>
        <el-form-item label="排序">
          <el-input-number v-model="form.sortNo" :min="0" />
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="form.remark" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="visible=false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="save">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { listDictOptions, saveDict } from '../api/dict'
import { fetchNav } from '../api/auth'
import { hasMenuAccess } from '../utils/nav'

const ADD_VALUE = '__DICT_ADD__'

export default {
  name: 'DictSelectWithAdd',
  props: {
    value: { type: [String, Number, Array], default: '' },
    dictType: { type: String, required: true },
    // 'dictLabel' 表示业务字段存名称，'id' 表示存字典项ID
    valueField: { type: String, default: 'dictLabel' },
    multiple: { type: Boolean, default: false },
    addText: { type: String, default: '添加选项' },
    labelPlaceholder: { type: String, default: '' },
    placeholder: { type: String, default: '请选择' },
    clearable: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    // 自定义选项加载；不传则按 dictType 拉字典选项
    optionsLoader: { type: Function, default: null }
  },
  data() {
    return {
      ADD_VALUE,
      options: [],
      filterQuery: '',
      visible: false,
      saving: false,
      form: {},
      navMenus: null
    }
  },
  computed: {
    filteredOptions() {
      const q = (this.filterQuery || '').trim().toLowerCase()
      if (!q) return this.options
      return this.options.filter(d => String(d.dictLabel || '').toLowerCase().includes(q))
    }
  },
  created() {
    this.reload()
  },
  watch: {
    dictType() {
      this.reload()
    },
    value: {
      immediate: true,
      handler() {
        this.keepLegacyValue()
      }
    }
  },
  methods: {
    optionValue(d) {
      return this.valueField === 'id' ? d.id : d[this.valueField]
    },
    // 业务字段存名称时，历史数据可能已不在字典里，补一条保证能正常显示
    keepLegacyValue() {
      if (this.valueField !== 'dictLabel') return
      const v = this.value
      if (!v || v === ADD_VALUE) return
      if (this.options.some(d => d.dictLabel === v)) return
      this.options = [{ id: 'legacy-' + v, dictLabel: v }, ...this.options]
    },
    onFilter(query) {
      this.filterQuery = query == null ? '' : String(query)
    },
    onVisibleChange(open) {
      if (!open) this.filterQuery = ''
    },
    async reload() {
      try {
        if (typeof this.optionsLoader === 'function') {
          const list = await this.optionsLoader()
          this.options = list || []
        } else {
          const res = await listDictOptions(this.dictType)
          this.options = res.data || []
        }
      } catch (e) {
        this.options = []
      }
      this.keepLegacyValue()
      this.$emit('options-loaded', this.options)
    },
    onSelectInput(v) {
      if (v === ADD_VALUE) return
      this.$emit('input', v)
    },
    onSelectChange(v) {
      if (v === ADD_VALUE) {
        this.$nextTick(() => {
          this.$emit('input', this.multiple ? (Array.isArray(this.value) ? this.value : []) : (this.value === ADD_VALUE ? '' : this.value))
        })
        this.openAdd()
        return
      }
      this.$emit('change', v)
    },
    async ensureNav() {
      if (this.navMenus) return this.navMenus
      const res = await fetchNav()
      this.navMenus = res.data || []
      return this.navMenus
    },
    async canManageDict() {
      const menus = await this.ensureNav()
      return hasMenuAccess(menus, { path: '/sys/dict', perms: 'sys:dict:list' })
    },
    async openAdd() {
      if (!(await this.canManageDict())) {
        return this.$message.warning(`没有字典管理权限，无法${this.addText}`)
      }
      this.form = {
        id: null,
        dictType: this.dictType,
        dictLabel: (this.filterQuery || '').trim(),
        dictValue: '',
        sortNo: (this.options.length + 1) * 10,
        remark: ''
      }
      this.visible = true
    },
    resetForm() {
      this.form = {}
      this.saving = false
    },
    async save() {
      const f = this.form
      if (!f.dictType || !f.dictLabel) return this.$message.warning('请填写选项名称')
      const label = f.dictLabel.trim()
      if (this.options.some(d => d.dictLabel === label)) {
        return this.$message.warning('同类型下选项名称已存在')
      }
      this.saving = true
      try {
        await saveDict({
          ...f,
          dictLabel: label,
          dictValue: (f.dictValue || '').trim() || null
        })
        this.$message.success('已保存')
        this.visible = false
        this.filterQuery = ''
        await this.reload()
        const added = this.options.find(d => d.dictLabel === label)
        if (added) {
          const v = this.optionValue(added)
          if (this.multiple) {
            const current = Array.isArray(this.value) ? [...this.value] : (this.value ? [this.value] : [])
            if (!current.includes(v)) current.push(v)
            this.$emit('input', current)
            this.$emit('change', current)
          } else {
            this.$emit('input', v)
            this.$emit('change', v)
          }
          this.$emit('added', added)
        }
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.dict-select-wrap {
  width: 100%;
}
</style>

<style>
/* 下拉挂到 body：固定高度 + 底部新增项吸底常显 */
.dict-select-dropdown.el-select-dropdown {
  max-height: none;
}
.dict-select-dropdown .el-select-dropdown__wrap {
  max-height: 220px !important;
}
.dict-select-dropdown .el-scrollbar__wrap {
  max-height: 220px !important;
}
.el-select-dropdown__item.dict-add-option {
  position: sticky;
  bottom: 0;
  z-index: 3;
  margin-top: 0;
  color: #409eff;
  font-weight: 500;
  background: #fff;
  border-top: 1px solid #ebeef5;
  box-shadow: 0 -4px 8px -4px rgba(15, 23, 42, 0.08);
}
.el-select-dropdown__item.dict-add-option .add-opt-text {
  display: block;
  width: 100%;
}
.el-select-dropdown__item.dict-add-option.hover,
.el-select-dropdown__item.dict-add-option:hover {
  background: #ecf5ff;
  color: #409eff;
}
</style>
